import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import type { ReasoningTrace } from './reasoningTrace';
import {
  FEEDBACK_KATEGORIEN,
  FEEDBACK_SYMPTOMS,
  FEEDBACK_SYMPTOM_LABELS,
  defaultKategorieForRating,
  feedbackFromTrace,
  formatFeedbackTimestampVienna,
  nowClientSubmittedAtIso,
  submitCustomerFeedback,
  type FeedbackDiagMeta,
  type FeedbackKategorie,
  type FeedbackRating,
  type FeedbackSymptom,
} from './portalCustomerFeedback';
import { FeedbackDiagCodeLine } from './FeedbackDiagCodeLine';

interface ArkoMessageFeedbackProps {
  customerId: string;
  trace?: ReasoningTrace | null;
  uiDiag?: FeedbackDiagMeta;
  /** Hauptreiter: bestehende Daumen ohne Wortmarke, in der Icon-Zeile. */
  iconsOnly?: boolean;
}

export const ArkoMessageFeedback: React.FC<ArkoMessageFeedbackProps> = ({
  customerId,
  trace,
  uiDiag,
  iconsOnly = false,
}) => {
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [kategorie, setKategorie] = useState<FeedbackKategorie>('Anregung');
  const [symptom, setSymptom] = useState<FeedbackSymptom>('other');
  const [text, setText] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [diagCode, setDiagCode] = useState<string | null>(null);
  /** ISO-8601 UTC — Zeitpunkt der Rating-Auswahl / Panel-Öffnung. */
  const [panelOpenedAt, setPanelOpenedAt] = useState<string | null>(null);

  const closePanel = useCallback(() => {
    if (pending) return;
    setRating(null);
    setText('');
    setError('');
    setSymptom('other');
    setPanelOpenedAt(null);
  }, [pending]);

  const openWith = useCallback((next: FeedbackRating) => {
    if (done || pending) return;
    setRating(next);
    setKategorie(defaultKategorieForRating(next));
    setSymptom('other');
    setError('');
    setPanelOpenedAt(nowClientSubmittedAtIso());
  }, [done, pending]);

  useEffect(() => {
    if (!rating || done || pending) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePanel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closePanel, done, pending, rating]);

  const handleSend = useCallback(async () => {
    if (!rating || pending || done) return;
    setPending(true);
    setError('');
    const clientSubmittedAt = nowClientSubmittedAtIso();
    try {
      const traceFeedback = feedbackFromTrace(trace);
      const result = await submitCustomerFeedback({
        customerId,
        kategorie,
        rating,
        nachricht: text,
        client_submitted_at: clientSubmittedAt,
        ...traceFeedback,
        diag: {
          ...(traceFeedback.diag ?? {}),
          ...(uiDiag ?? {}),
          symptom,
        },
      });
      setDiagCode(result.diagCode);
      setDone(true);
      setPanelOpenedAt(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feedback konnte nicht gespeichert werden.');
    } finally {
      setPending(false);
    }
  }, [customerId, done, kategorie, pending, rating, symptom, text, trace, uiDiag]);

  if (done) {
    return (
      <div className={iconsOnly ? 'space-y-1' : 'mt-1 space-y-1 pl-8'}>
        <p className="text-[10px] text-emerald-500/80">Danke — Feedback gespeichert.</p>
        <FeedbackDiagCodeLine diagCode={diagCode} compact />
      </div>
    );
  }

  const viennaLabel = panelOpenedAt ? formatFeedbackTimestampVienna(panelOpenedAt) : '';
  const thumbClass = (active: boolean) => (
    iconsOnly
      ? `inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
        active
          ? 'bg-white/[0.08] text-slate-100'
          : 'text-slate-500 hover:bg-white/[0.06] hover:text-slate-200'
      }`
      : `inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition-colors ${
        active
          ? 'border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-200'
          : 'border-white/[0.08] text-slate-500 hover:text-slate-200'
      }`
  );

  return (
    <div className={iconsOnly ? 'flex min-w-0 flex-1 flex-col items-stretch' : 'mt-1 w-full max-w-[min(100%,28rem)] pl-8'}>
      <div className="flex flex-wrap items-center gap-0.5">
        {iconsOnly ? null : <span className="mr-1 text-[10px] text-slate-600">Antwort</span>}
        <button
          type="button"
          disabled={pending}
          onClick={() => openWith('gut')}
          className={iconsOnly ? thumbClass(rating === 'gut') : `inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition-colors ${
            rating === 'gut'
              ? 'border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-200'
              : 'border-white/[0.08] text-slate-500 hover:text-slate-200'
          }`}
          {...(iconsOnly ? { 'aria-label': 'Gut', title: 'Gut' } : {})}
        >
          <ThumbsUp className={iconsOnly ? 'h-3.5 w-3.5' : 'h-3 w-3'} aria-hidden />
          {iconsOnly ? null : 'Gut'}
        </button>
        <button
          type="button"
          disabled={pending}
          onClick={() => openWith('schlecht')}
          className={iconsOnly
            ? `inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
              rating === 'schlecht'
                ? 'bg-white/[0.08] text-slate-100'
                : 'text-slate-500 hover:bg-white/[0.06] hover:text-slate-200'
            }`
            : `inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition-colors ${
              rating === 'schlecht'
                ? 'border-amber-400/30 bg-amber-400/[0.08] text-amber-100'
                : 'border-white/[0.08] text-slate-500 hover:text-slate-200'
            }`}
          {...(iconsOnly ? { 'aria-label': 'Schlecht', title: 'Schlecht' } : {})}
        >
          <ThumbsDown className={iconsOnly ? 'h-3.5 w-3.5' : 'h-3 w-3'} aria-hidden />
          {iconsOnly ? null : 'Schlecht'}
        </button>
      </div>
      {rating ? (
        <div className="mt-1.5 space-y-1.5 rounded-xl border border-white/[0.08] bg-black/20 p-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {viennaLabel ? (
                <p className="mb-1 text-[10px] tabular-nums text-slate-500" title={panelOpenedAt ?? undefined}>
                  {viennaLabel}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-1">
                {FEEDBACK_KATEGORIEN.map((item) => (
                  <button
                    key={item}
                    type="button"
                    disabled={pending}
                    onClick={() => setKategorie(item)}
                    className={`rounded-lg px-2 py-0.5 text-[10px] transition-colors ${
                      kategorie === item
                        ? 'bg-white/[0.08] text-slate-100'
                        : 'text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              disabled={pending}
              onClick={closePanel}
              aria-label="Feedback schließen"
              title="Schließen"
              className="inline-flex shrink-0 items-center justify-center rounded-lg border border-white/[0.08] p-1 text-slate-500 transition-colors hover:bg-white/[0.06] hover:text-slate-200 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Symptom
            </p>
            <div className="flex flex-wrap gap-1">
              {FEEDBACK_SYMPTOMS.map((item) => (
                <button
                  key={item}
                  type="button"
                  disabled={pending}
                  onClick={() => setSymptom(item)}
                  className={`rounded-lg border px-2 py-0.5 text-[10px] transition-colors ${
                    symptom === item
                      ? 'border-cyan-400/30 bg-cyan-400/[0.08] text-cyan-100'
                      : 'border-white/[0.06] text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {FEEDBACK_SYMPTOM_LABELS[item]}
                </button>
              ))}
            </div>
          </div>
          <textarea
            value={text}
            maxLength={1000}
            rows={2}
            disabled={pending}
            onChange={(event) => setText(event.target.value)}
            placeholder="Optionaler Hinweis — keine Mandantennamen oder Chat-Volltext."
            className="w-full resize-y rounded-lg border border-white/[0.08] bg-black/30 px-2 py-1.5 text-[11px] text-slate-200 outline-none placeholder:text-slate-600"
          />
          {error ? <p className="text-[10px] text-rose-300">{error}</p> : null}
          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              disabled={pending}
              onClick={closePanel}
              aria-label="Feedback schließen"
              className="rounded-lg border border-white/[0.08] px-2 py-1 text-[10px] text-slate-400 transition-colors hover:bg-white/[0.04] hover:text-slate-200 disabled:opacity-50"
            >
              Schließen
            </button>
            <button
              type="button"
              disabled={pending}
              onClick={() => void handleSend()}
              className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] bg-white/[0.04] px-2 py-1 text-[10px] text-slate-200 hover:bg-white/[0.08] disabled:opacity-50"
            >
              {pending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
              Senden
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
